import { useMemo } from 'react';
import { Cell, Pie, PieChart, Sector } from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from '@/components/ui/chart';
import { categoryDef } from '@/lib/constants';
import { formatVNDShort } from '@/lib/format';
import type { ReportSegment } from '@/lib/api';

// Semantic palette — each category keeps its identity hue, softened to mid-saturation/mid-lightness.
// Each slice gets a vertical gradient (top opaque → bottom translucent) for that "polished SaaS" feel.
const CATEGORY_COLOR: Record<string, string> = {
  focal: 'hsl(262 50% 65%)',     // soft lavender
  oled: 'hsl(199 60% 58%)',      // soft sky
  gaming: 'hsl(0 58% 66%)',      // soft coral
  graphic: 'hsl(160 42% 52%)',   // soft mint
  curved: 'hsl(38 70% 62%)',     // soft amber
  popular: 'hsl(220 14% 62%)',   // soft slate
  portable: 'hsl(258 55% 70%)',  // soft lilac
};

const FALLBACK = 'hsl(220 12% 70%)';

export type DonutMetric = 'revenue' | 'units';

export interface ReportCategoryDonutProps {
  segments: ReportSegment[];
  /** 'revenue' (default) → revenue_vnd; 'units' → units_sold */
  metric?: DonutMetric;
  /** Unique id prefix so multiple donuts on same page don't clash on gradient ids */
  idPrefix?: string;
}

export function ReportCategoryDonut({ segments, metric = 'revenue', idPrefix = 'donut' }: ReportCategoryDonutProps) {
  const data = useMemo(() => {
    const totalForShare =
      metric === 'revenue'
        ? segments.reduce((s, x) => s + x.revenue_vnd, 0)
        : segments.reduce((s, x) => s + x.units_sold, 0);

    return segments
      .map((s) => {
        const def = categoryDef(s.category);
        const value = metric === 'revenue' ? s.revenue_vnd : s.units_sold;
        const baseColor = CATEGORY_COLOR[s.category] ?? FALLBACK;
        return {
          category: s.category,
          label: def?.label ?? s.category,
          value,
          share: totalForShare > 0 ? (value / totalForShare) * 100 : 0,
          baseColor,
          gradientId: `${idPrefix}-${metric}-${s.category}`,
        };
      })
      .filter((d) => d.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [segments, metric, idPrefix]);

  const total = useMemo(() => data.reduce((sum, d) => sum + d.value, 0), [data]);

  const config = useMemo<ChartConfig>(() => {
    const cfg: ChartConfig = {};
    for (const d of data) {
      cfg[d.category] = { label: d.label, color: d.baseColor };
    }
    return cfg;
  }, [data]);

  if (data.length === 0) {
    return (
      <div className="rounded-lg border border-dashed bg-muted/30 p-8 text-center text-sm text-muted-foreground">
        Chưa có dữ liệu để vẽ donut.
      </div>
    );
  }

  const isRevenue = metric === 'revenue';
  const formatValue = (v: number) => (isRevenue ? formatVNDShort(v) : v.toLocaleString('vi-VN'));
  const totalLabel = isRevenue ? `Tổng doanh thu · ${formatVNDShort(total)}` : `Tổng số lượng · ${total.toLocaleString('vi-VN')}`;

  return (
    <div className="flex flex-col items-center gap-3">
      <ChartContainer config={config} className="aspect-square h-[180px] w-[180px]">
        <PieChart>
          <defs>
            {data.map((d) => (
              <linearGradient key={d.gradientId} id={d.gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={d.baseColor} stopOpacity={0.95} />
                <stop offset="100%" stopColor={d.baseColor} stopOpacity={0.55} />
              </linearGradient>
            ))}
          </defs>
          <ChartTooltip
            cursor={false}
            content={
              <ChartTooltipContent
                hideLabel
                formatter={(value, name, item) => {
                  const num = typeof value === 'number' ? value : Number(value);
                  const p = item.payload as typeof data[number] | undefined;
                  return (
                    <div className="flex w-full items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <div className="h-2.5 w-2.5 rounded-[2px]" style={{ background: p?.baseColor }} />
                        <span className="text-muted-foreground">{p?.label ?? (name as string)}</span>
                      </div>
                      <span className="font-medium tabular-nums text-foreground">
                        {formatValue(num)} <span className="ml-1 text-[10px] text-muted-foreground">({p?.share?.toFixed(1)}%)</span>
                      </span>
                    </div>
                  );
                }}
              />
            }
          />
          <Pie
            data={data}
            dataKey="value"
            nameKey="category"
            innerRadius={54}
            outerRadius={80}
            strokeWidth={3}
            stroke="hsl(var(--background))"
            paddingAngle={3}
            cornerRadius={6}
            activeShape={(props: object) => <Sector {...(props as Parameters<typeof Sector>[0])} outerRadius={(props as { outerRadius: number }).outerRadius + 4} />}
          >
            {data.map((d) => (
              <Cell key={d.category} fill={`url(#${d.gradientId})`} />
            ))}
          </Pie>
        </PieChart>
      </ChartContainer>

      <div className="w-full space-y-1">
        <div className="text-center text-[10px] uppercase tracking-wider text-muted-foreground">
          {totalLabel}
        </div>
        <div className="space-y-1 pt-1">
          {data.map((d) => (
            <div key={d.category} className="flex items-center gap-2 text-[11px]">
              <div className="h-2 w-2 shrink-0 rounded-[2px]" style={{ background: d.baseColor }} />
              <span className="flex-1 truncate font-medium">{d.label}</span>
              <span className="tabular-nums text-muted-foreground">{formatValue(d.value)}</span>
              <span className="w-10 text-right tabular-nums font-semibold">{d.share.toFixed(1)}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
